import type { ValidCurrencyLowercase } from '@/types/plans';

export type FallbackPlanKey = 'essentials' | 'creator' | 'pro' | 'business';

interface PlanFallback {
  monthly: number;
  yearly: number;
}

/**
 * Phase 1 winner display prices per (plan × currency) in major units.
 * Used as a fallback when the `/admin/pricing` API doesn't return pricing
 * for the user's currency — currently the case for INR/BRL/AUD until
 * DEV-5672 ships the Stripe + Retool config. Once the backend has all 7
 * currencies, this map is dead code and can be deleted.
 *
 * Keep this in sync with the Phase 1 winners CSV
 * (.context/attachments/Untitled spreadsheet - Phase 1 Winners _ New Controls.csv).
 */
export const PRICING_FALLBACK: Record<
  FallbackPlanKey,
  Record<ValidCurrencyLowercase, PlanFallback>
> = {
  essentials: {
    usd: { monthly: 6.99, yearly: 67.08 },
    gbp: { monthly: 8.99, yearly: 86.28 },
    eur: { monthly: 9.99, yearly: 95.88 },
    cad: { monthly: 12.99, yearly: 131.88 },
    aud: { monthly: 9.99, yearly: 95.88 },
    brl: { monthly: 34.99, yearly: 335.88 },
    inr: { monthly: 599, yearly: 5748 },
  },
  creator: {
    usd: { monthly: 9.99, yearly: 95.88 },
    gbp: { monthly: 12.99, yearly: 107.88 },
    eur: { monthly: 14.99, yearly: 119.88 },
    cad: { monthly: 19.99, yearly: 155.88 },
    aud: { monthly: 13.99, yearly: 131.88 },
    brl: { monthly: 49.99, yearly: 479.88 },
    inr: { monthly: 899, yearly: 8388 },
  },
  pro: {
    usd: { monthly: 23.99, yearly: 167.88 },
    gbp: { monthly: 24.99, yearly: 179.88 },
    eur: { monthly: 32.99, yearly: 191.88 },
    cad: { monthly: 44.99, yearly: 263.88 },
    aud: { monthly: 33.99, yearly: 239.88 },
    brl: { monthly: 119.99, yearly: 839.88 },
    inr: { monthly: 2199, yearly: 15_588 },
  },
  // Monthly figures from DEV-7423 (the `business-<currency>-month` Stripe prices).
  business: {
    usd: { monthly: 59.99, yearly: 419.88 },
    gbp: { monthly: 45.99, yearly: 335.88 },
    eur: { monthly: 49.99, yearly: 359.88 },
    cad: { monthly: 99.99, yearly: 599.88 },
    aud: { monthly: 84.99, yearly: 599.88 },
    brl: { monthly: 289.99, yearly: 2039.88 },
    inr: { monthly: 4999, yearly: 29_988 },
  },
};

export const getFallbackPrice = (
  plan: FallbackPlanKey,
  currencyCode: string,
  period: 'monthly' | 'yearly'
): number => {
  const lower = currencyCode.toLowerCase() as ValidCurrencyLowercase;
  const entry = PRICING_FALLBACK[plan][lower] ?? PRICING_FALLBACK[plan].usd;
  return entry[period];
};
